import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Connected.css';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../config/apiConfig';

const Connected = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { status, error, message, accountName, accountId } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = (params.get('status') || 'success').toLowerCase();
    const errorParam = params.get('error');
    const messageParam = params.get('message');
    const accountNameParam = params.get('accountName');
    const accountIdParam = params.get('accountId');
    return {
      status: statusParam,
      error: errorParam,
      message: messageParam,
      accountName: accountNameParam,
      accountId: accountIdParam,
    };
  }, [location.search]);

  const isSuccess = !error && status === 'success';
  const [countdown, setCountdown] = useState(5);
  const [refreshStatus, setRefreshStatus] = useState('loading'); // loading | success | error

  const refreshDatasets = useCallback(async () => {
    const storedProfile = localStorage.getItem('user_profile');
    if (!storedProfile) {
      setRefreshStatus('error');
      return;
    }

    try {
      const { orgId } = JSON.parse(storedProfile);
      if (!orgId) {
        setRefreshStatus('error');
        return;
      }
      await apiService.post(API_ENDPOINTS.REFRESH_DATASETS(orgId), null, { timeout: 0 });
      setRefreshStatus('success');
    } catch (err) {
      console.error('Failed to refresh datasets after connection:', err);
      setRefreshStatus('error');
    }
  }, []);

  useEffect(() => {
    if (isSuccess) {
      const payload = {
        connected: true,
        connectedAt: new Date().toISOString(),
        accountName: accountName || null,
        accountId: accountId || null,
      };
      try {
        localStorage.setItem('meta_connection_status', JSON.stringify(payload));
      } catch (storageError) {
        console.warn('Unable to persist meta connection status:', storageError);
      }
    } else {
      localStorage.removeItem('meta_connection_status');
    }
  }, [isSuccess, accountName, accountId]);

  useEffect(() => {
    if (!isSuccess) {
      setRefreshStatus('error');
      return;
    }
    refreshDatasets();
  }, [isSuccess, refreshDatasets]);

  useEffect(() => {
    if (!isSuccess || refreshStatus !== 'success') {
      return undefined;
    }

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const timeout = setTimeout(() => {
      navigate('/dashboard', { replace: true });
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isSuccess, refreshStatus, navigate]);

  const renderCardContent = () => {
    if (!isSuccess) {
      return (
        <>
          <div className="connected-icon error">
            <i className="fas fa-exclamation" />
          </div>
          <h1 className="connected-title">Connection Issue</h1>
          <p className="connected-message">
            {message ||
              error ||
              'We were unable to verify the Meta callback response.'}
          </p>
          <p className="connected-subtext">
            You can retry the connection or head back to your dashboard.
          </p>
        </>
      );
    }

    if (refreshStatus === 'loading') {
      return (
        <>
          <div className="connected-icon success">
            <i className="fas fa-plug" />
          </div>
          <h1 className="connected-title">Connected to Meta</h1>
          <p className="connected-message">
            Syncing your datasets with Meta… Please hold tight while we fetch the latest pixels.
          </p>
          <div className="connected-loading-indicator">
            <div className="connected-loading-spinner" />
            <span>Fetching pixels from Meta</span>
          </div>
          {accountName || accountId ? (
            <div className="connected-account-card">
              <div className="connected-account-label">Meta Account Linked</div>
              {accountName && <div className="connected-account-name">{accountName}</div>}
              {accountId && (
                <div className="connected-account-id">
                  Ad Account ID: <span>{accountId}</span>
                </div>
              )}
            </div>
          ) : null}
        </>
      );
    }

    return (
      <>
        <div className="connected-icon success">
          <i className="fas fa-check" />
        </div>
        <h1 className="connected-title">Connected to Meta</h1>
        <p className="connected-message">
          {(message && message.trim()) ||
            'Meta account linked successfully. Pixels and datasets are ready in your dashboard.'}
        </p>
        {accountName || accountId ? (
          <div className="connected-account-card">
            <div className="connected-account-label">Connected Meta Account</div>
            {accountName && <div className="connected-account-name">{accountName}</div>}
            {accountId && (
              <div className="connected-account-id">
                Ad Account ID: <span>{accountId}</span>
              </div>
            )}
          </div>
        ) : null}
        {refreshStatus === 'success' ? (
          <p className="connected-subtext">
            Redirecting to dashboard in <span>{countdown}</span> seconds…
          </p>
        ) : (
          <p className="connected-subtext connected-subtext--warning">
            We connected your account, but syncing datasets failed. You can still continue to the
            dashboard and retry manually.
          </p>
        )}
      </>
    );
  };

  const handleContinue = () => {
    navigate('/dashboard', { replace: true });
  };

  return (
    <div className={`connected-container ${isSuccess ? 'success' : 'error'}`}>
      <div className="connected-card">
        {renderCardContent()}
      </div>
    </div>
  );
};

export default Connected;

